import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { vehicleContext } from '../VehicleProvider';
import usePalette from '../../hooks/usePalette';
import InfoPopover from '../InfoPopover';
import {
  StatusContainer,
  Status,
  StatusDot,
  StatusTitle,
  InfoIconWrapper,
} from './VehicleStatusInfo.style';
import { IS_T3_WEBSITE } from '../../constants/environment';
import {
  SYS_COLOR_SURFACE_SIGNAL_SUCCESS_100_LIGHT,
  SYS_COLOR_SURFACE_SIGNAL_NEGATIVE_LIGHT,
  SYS_COLOR_SURFACE_SIGNAL_PENDING_LIGHT,
} from '@oneaudi/unified-web-common';

const VehicleStatusInfo: React.FC = () => {
  const { t } = useTranslation();

  const { vehicleData, market } = useContext(vehicleContext);
  const nonAudiLocationFlag = IS_T3_WEBSITE && vehicleData?.nonAudiDealershipLocation.multiLocation;
  const { statusTextColor, statusBgColor, popUpBgColor } = usePalette(vehicleData);
  const isCanadian = market === 'ca' || market === 'cauc';

  const vehicleStatus = useMemo(() => {
    let label = '';
    let dotColor = SYS_COLOR_SURFACE_SIGNAL_PENDING_LIGHT; // Default color
    let popoverDescription = t(
      `vdpHeader.availableSoon.availableSoonPopUpInfo${isCanadian ? 'CAN' : 'USA'}`,
    );

    if (vehicleData) {
      if (nonAudiLocationFlag) {
        label = t(`vdpHeader.nonAudiLocation`);
        dotColor = SYS_COLOR_SURFACE_SIGNAL_SUCCESS_100_LIGHT;
        popoverDescription = t('vdpHeader.nonAudiLocationPopUpInfo');
      } else if (vehicleData.vehicleType === 'new' && vehicleData.saleOrderType?.includes('SOLD')) {
        label = t(`vdpHeader.reserved`);
        dotColor = SYS_COLOR_SURFACE_SIGNAL_NEGATIVE_LIGHT;
        popoverDescription = t('vdpHeader.reservedPopUpInfo');
      } else if (vehicleData.isAvailableNow) {
        label = t(`vdpHeader.availableSoon.availableNow${isCanadian ? 'CAN' : 'USA'}`);
        popoverDescription = t(`vdpHeader.availableNowPopUpInfo${isCanadian ? 'CAN' : 'USA'}`);
        dotColor = SYS_COLOR_SURFACE_SIGNAL_SUCCESS_100_LIGHT;
      } else if (vehicleData.vehicleOrderStatus === 'Intransit From Factory') {
        label = t('vdpHeader.availableSoon.orderStatus.factory');
      } else if (vehicleData.vehicleOrderStatus === 'Port Stock') {
        label = t('vdpHeader.availableSoon.orderStatus.port');
      } else if (vehicleData.vehicleOrderStatus === 'Intransit Inland') {
        label = t('vdpHeader.availableSoon.orderStatus.inland');
      } else if (!vehicleData.vehicleOrderStatus) {
        label = t('vdpHeader.availableSoon.orderStatus.contactDealer');
      }
    }

    return { label, dotColor, popoverDescription };
  }, [t, isCanadian, vehicleData, nonAudiLocationFlag]);

  return (
    <StatusContainer data-testid="vehicle-status-info">
      <Status backColor={statusBgColor} textColor={statusTextColor}>
        <StatusDot dotColor={vehicleStatus.dotColor} />
        <StatusTitle data-testid="status">{vehicleStatus.label}</StatusTitle>
        <InfoIconWrapper>
          <InfoPopover
            id="info-popover"
            description={vehicleStatus.popoverDescription}
            iconSize={'xs'}
            triggerTestId="popover-icon"
            iconColor={statusTextColor}
            bgColor={popUpBgColor}
          />
        </InfoIconWrapper>
      </Status>
    </StatusContainer>
  );
};

export default VehicleStatusInfo;
